// Pure aggregation helpers for the dashboard. Kept free of Svelte/DOM so the
// income/expense totals, the income-progress comparison, the percent-of-income
// label, and the by-type grouping can be unit-tested directly. The route keeps
// only the reactive wiring around these.

import { round2 } from './format';
import { isUncategorised } from './category';
import type { ExpectedIncome } from './schedule';
import type { CategoryBudgetItem } from './budget';
import type { Transaction, Category } from './db';

export interface Aggregates {
	totalIncome: number;
	totalExpenses: number; // negative
	netBalance: number;
	uncategorisedCount: number;
}

/**
 * Month totals over the budget-relevant transactions (transfers and
 * budget-excluded rows skipped). Income is the sum of positive amounts,
 * expenses the sum of negatives, net their sum.
 */
export function computeAggregates(transactions: Transaction[]): Aggregates {
	let totalIncome = 0;
	let totalExpenses = 0;
	let uncategorisedCount = 0;

	for (const tx of transactions) {
		if (tx.isTransfer || tx.excludeFromBudget) continue;
		if (isUncategorised(tx.category)) uncategorisedCount += 1;
		if (tx.amount > 0) totalIncome += tx.amount;
		else if (tx.amount < 0) totalExpenses += tx.amount;
	}

	return { totalIncome, totalExpenses, netBalance: totalIncome + totalExpenses, uncategorisedCount };
}

/** name → category type, for tagging budget rows by type. Untyped names omitted. */
export function buildCategoryTypeMap(categories: Category[]): Map<string, string> {
	const map = new Map<string, string>();
	for (const cat of categories) {
		if (cat.type) map.set(cat.name, cat.type);
	}
	return map;
}

/**
 * `amount` as a percentage of income, or "" when there's nothing to divide by.
 * One decimal under 10% (so a $200 payment against $25k reads "0.8%", not "1%"),
 * whole numbers above.
 */
export function formatPctOfIncome(amount: number, income: number): string {
	if (income <= 0 || amount <= 0) return '';
	const pct = (amount / income) * 100;
	return pct < 10 ? `${round2(pct).toFixed(1)}% of income` : `${Math.round(pct)}% of income`;
}

export interface IncomeProgress {
	label: string;
	value: number;
	isBehind: boolean;
	remaining: number;
}

/**
 * Income progress for the header: prefer the scheduled "expected so far" figure,
 * fall back to a flat planned figure, or null when neither is configured.
 * `actualIncome` is the realised income (aggregates.totalIncome).
 */
export function computeIncomeProgress(
	expected: ExpectedIncome,
	plannedIncome: number,
	actualIncome: number
): IncomeProgress | null {
	if (expected.expectedTotal > 0) {
		const remaining = round2(expected.expectedTotal - expected.expectedToDate);
		return {
			label: 'expected so far',
			value: expected.expectedToDate,
			isBehind: actualIncome < expected.expectedToDate,
			remaining: remaining > 0 ? remaining : 0,
		};
	}
	if (plannedIncome > 0) {
		return {
			label: 'planned',
			value: plannedIncome,
			isBehind: actualIncome < plannedIncome,
			remaining: 0,
		};
	}
	return null;
}

export interface BudgetTypeRow {
	type: string;
	budgeted: number;
	spent: number;
	count: number;
	categories: string[];
	percentUsed: number;
	isOverBudget: boolean;
}

/**
 * Roll the per-category budget items up by category type ("Uncategorised" for
 * untyped names), then sort by type name (when sortBy === 'type') or by spend.
 */
export function groupBudgetByType(
	items: CategoryBudgetItem[],
	typeMap: Map<string, string>,
	sortBy: 'amount' | 'name' | 'type'
): BudgetTypeRow[] {
	const grouped = new Map<string, { budgeted: number; spent: number; count: number; categories: string[] }>();

	for (const item of items) {
		const type = typeMap.get(item.name) || 'Uncategorised';
		const existing = grouped.get(type) || { budgeted: 0, spent: 0, count: 0, categories: [] };
		existing.budgeted += item.budgeted;
		existing.spent += item.spent;
		existing.count += item.count;
		existing.categories.push(item.name);
		grouped.set(type, existing);
	}

	const rows = Array.from(grouped.entries()).map(([type, data]) => ({
		type,
		budgeted: round2(data.budgeted),
		spent: round2(data.spent),
		count: data.count,
		categories: data.categories,
		percentUsed: data.budgeted > 0 ? round2((data.spent / data.budgeted) * 100) : data.spent > 0 ? 100 : 0,
		isOverBudget: data.budgeted > 0 && data.spent > data.budgeted,
	}));

	if (sortBy === 'type') return rows.sort((a, b) => a.type.localeCompare(b.type));
	return rows.sort((a, b) => b.spent - a.spent);
}
